/**
 * Weather.com Radar Timestamp Service
 * Finds the latest available mosaic tile at five-minute time slices.
 */

import {
  buildWeathercomTileUrl,
  type RadarTimestamp,
} from '@/utils/radarProvider';

const FRAME_COUNT = 12;
const INTERVAL_SECONDS = 5 * 60;
const MAX_PROBE_LOOKBACK = 2;

/** Build the existing 12-frame timeline ending at a verified tile time. */
function framesEndingAt(latestEpoch: number): RadarTimestamp[] {
  const frames: RadarTimestamp[] = [];
  for (let i = FRAME_COUNT - 1; i >= 0; i--) {
    frames.push({
      source: 'weathercom',
      epoch: latestEpoch - i * INTERVAL_SECONDS,
    });
  }
  return frames;
}

/** Check recent slots, stopping when a previously verified frame is reached. */
async function probeLatestTileEpoch(previousLatestEpoch?: number): Promise<number> {
  const roundedNow = Math.floor(Date.now() / 1000 / INTERVAL_SECONDS) * INTERVAL_SECONDS;
  const usablePreviousEpoch =
    typeof previousLatestEpoch === 'number' &&
    Number.isSafeInteger(previousLatestEpoch) &&
    previousLatestEpoch <= roundedNow &&
    roundedNow - previousLatestEpoch < FRAME_COUNT * INTERVAL_SECONDS
      ? previousLatestEpoch
      : null;

  for (let offset = 0; offset <= MAX_PROBE_LOOKBACK; offset++) {
    const epoch = roundedNow - offset * INTERVAL_SECONDS;
    if (usablePreviousEpoch !== null && epoch <= usablePreviousEpoch) {
      return usablePreviousEpoch;
    }

    const url = buildWeathercomTileUrl({ x: 0, y: 0 }, 1, epoch);
    let response: Response;
    try {
      response = await fetch(url, { method: 'HEAD' });
    } catch {
      break;
    }
    if (response.ok && response.headers.get('Content-Type')?.startsWith('image/')) {
      return epoch;
    }
    if (response.status !== 400) break;
  }

  if (usablePreviousEpoch !== null) return usablePreviousEpoch;
  throw new Error('Weather.com has no available recent radar tile');
}

/** End the timeline at the newest tile confirmed by the proxy. */
export async function fetchWeathercomRadarTimestamps(previousLatestEpoch?: number): Promise<RadarTimestamp[]> {
  const latestEpoch = await probeLatestTileEpoch(previousLatestEpoch);
  return framesEndingAt(latestEpoch);
}
